import { create } from "zustand";
import { devtools } from "zustand/middleware";

import { hasPreviousSession, RAGCHAT_DEFAULTS } from "../utils/ragchatStorage";
import { createChunkingActions } from "./actions/chatChunking";
import { createDataLoaderActions } from "./actions/chatDataLoader";
import { createMessageActions } from "./actions/chatMessages";
import { createQueryActions } from "./actions/chatQuery";
import { createStreamingActions } from "./actions/chatStreaming";
import { createUIActions } from "./actions/chatUI";
import { createUserPreferencesActions } from "./actions/chatUserPreferences";
import {
  AnswerMode,
  ChatActions,
  ChatMessage,
  ChatState,
  ChatStore,
  ContextDataItem,
  MessageHistory
} from "./chatTypes";

export const useChatStore = create<ChatStore>()(
  devtools(
    (set, get) => ({
      // Initial state
      messages: [],
      messageHistory: [],
      inputValue: "",
      editingMessage: null,
      isDrawerOpen: false,
      loading: false,
      contextData: null,
      isContextLoaded: false,
      includedArtifactTypes: RAGCHAT_DEFAULTS.includedArtifactTypes,
      totalTokens: 0,
      pendingQuery: null,
      lastSelectedContext: { interactionIds: [], filter: null },
      permittedEmails: null,
      tenantId: null,

      // Token limit computed properties initial state
      exceedsTokenLimit: false,
      isBlockedByTokenLimit: false,

      // Token length state for optimization
      totalCalculatedTokens: 0,
      singleContextTokenLength: 0,

      // Chunking state
      isChunkingEnabled: false,
      contextChunks: [],
      continueChunkedQuery: null,
      isCreatingChunks: false,

      // Query Execution initial state
      isExecuting: false,
      currentAbortController: null,

      // User Preferences initial state (defaults; loaded from localStorage when feature flag is on)
      temperature: RAGCHAT_DEFAULTS.temperature,
      thinkingMode: RAGCHAT_DEFAULTS.thinkingMode,
      maxTokens: RAGCHAT_DEFAULTS.maxTokens,
      budgetTokens: RAGCHAT_DEFAULTS.budgetTokens,
      maxInputTokens: RAGCHAT_DEFAULTS.maxInputTokens,
      model: RAGCHAT_DEFAULTS.model,
      effort: RAGCHAT_DEFAULTS.effort,
      answerMode: RAGCHAT_DEFAULTS.answerMode,
      lastInjectedAnswerMode: null,

      // Feature Flags initial state - will be updated by components when they load
      thinkingModeEnabled: false,

      // Previous session
      viewingPreviousSession: false,
      previousSessionMessages: [],
      hasSavedPreviousSession: hasPreviousSession(),

      // Spread all actions from the extracted files
      ...createUIActions(set, get),
      ...createMessageActions(set, get),
      ...createDataLoaderActions(set, get),
      ...createUserPreferencesActions(set, get),
      ...createChunkingActions(set, get),
      ...createQueryActions(set, get),
      ...createStreamingActions(set),

      // Feature Flag actions
      setThinkingModeEnabled: (enabled: boolean) => set({ thinkingModeEnabled: enabled })
    }),
    {
      name: "chat-store"
    }
  )
);

export type { AnswerMode, ChatMessage, MessageHistory, ChatState, ChatActions, ContextDataItem };
